
import Vue from 'vue';
import { mapActions } from 'vuex';
import BaseButton from '@/components/base/BaseButton.vue';
import { TeamUserMixin } from '@/mixins';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { ROUTER } from '@/router/constants';
import { auth } from '@/firebase';
import { DomainClaimCta, DomainClaimDialogContent } from '@/utils/domainClaimRestriction';

/**
 * Self-contained content for the Domain Claim Resolution Dialog. Renders the branched
 * copy + CTAs and performs each CTA's action itself (switch account, recheck, change
 * email, manage/downgrade plan, open the full picker, sign in as someone else). Emits
 * `close` when the host container should close, and `dismiss` for the snooze action.
 * Used by both the sidenav flyout and the centered-modal fallback.
 */
export default Vue.extend({
	name: 'DomainClaimResolutionContent',
	mixins: [
		TeamUserMixin,
	],
	components: {
		BaseButton,
	},
	props: {
		content: {
			type: Object as () => DomainClaimDialogContent,
			required: true,
		},
	},
	data () {
		return {
			seeAllTimer: null as ReturnType<typeof setTimeout> | null,
		};
	},
	beforeDestroy () {
		if (this.seeAllTimer) {
			clearTimeout(this.seeAllTimer);
			this.seeAllTimer = null;
		}
	},
	computed: {
		primaryCta (): DomainClaimCta | null {
			return this.content.ctas.find((cta: DomainClaimCta) => cta.primary) || null;
		},
		secondaryCtas (): DomainClaimCta[] {
			return this.content.ctas.filter((cta: DomainClaimCta) => !cta.primary);
		},
	},
	methods: {
		...mapActions([
			'updateToolbar',
			'bindAllTeamData',
			'incrementOpenRunnitAccountSelectionMenuTrigger',
		]),
		goToSubscriptions () {
			if (this.$route.name !== ROUTER.RUNNITS_SUBSCRIPTIONS) {
				this.$router.push({ name: ROUTER.RUNNITS_SUBSCRIPTIONS }).catch(() => { /* redundant nav */ });
			}
		},
		handleCta (cta: DomainClaimCta) {
			switch (cta.kind) {
				case 'switch': {
					const teamId = this.content.recommendedTeam?.teamId;
					if (teamId) {
						this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.TEAM);
						this.bindAllTeamData(teamId);
					}
					this.$emit('close');
					break;
				}
				case 'recheck':
					// captureSignInData re-runs on boot -> refreshes matchingTeams.
					window.location.reload();
					break;
				case 'changeEmail':
					this.updateToolbar({ changeLoginSettingsDialogConfig: { open: true } });
					this.$emit('close');
					break;
				case 'managePlan':
				case 'downgrade':
					this.goToSubscriptions();
					this.$emit('close');
					break;
				case 'seeAll':
					this.$emit('close');
					// The account picker is anchored inside the sidenav; on desktop ensure the
					// sidenav is open so the picker has a visible anchor (on mobile it opens as an
					// anchorless bottom sheet, so no sidenav is needed). This makes 'See all
					// accounts' work from the centered-modal host on non-sidenav routes too.
					if (this.$vuetify.breakpoint.mdAndUp) {
						this.updateToolbar({ runnitsSideNavOpen: true });
					}
					// Defer so the originating click + flyout-close settle before the picker opens,
					// else that same click is seen as an outside-click and instantly dismisses it.
					this.seeAllTimer = window.setTimeout(() => this.incrementOpenRunnitAccountSelectionMenuTrigger(), 150);
					break;
				case 'signInOther':
					this.signInWithAnotherEmail();
					break;
				case 'dismiss':
				default:
					this.$emit('dismiss');
					break;
			}
		},
		async signInWithAnotherEmail () {
			try {
				await auth.signOut();
			} catch (e) {
				console.error('Failed to sign out for re-login:', e);
			}
			window.location.assign('/');
		},
	},
});
